import { useCallback, useEffect, useState } from 'react';
import { intelSaveScenario, intelScenarios, type ApiClient, type SavedScenario } from '@aithora/shared';
import type { Deal } from '../model';

export type ScenarioDraft = Omit<SavedScenario, 'id' | 'facilityKey' | 'createdAt'>;

/** The key a facility's saved scenarios are filed under: its catalog id, else its deal-book id. */
export const scenarioKey = (f: Deal) => f.providerId ?? f.id;

/**
 * Saved Deal model scenarios for one facility, kept per person on the server
 * (GET / POST /intelligence/scenarios). If the server cannot be reached the list says so
 * and nothing is shown as saved.
 */
export function useSavedScenarios(client: ApiClient, f: Deal, readOnly: boolean) {
  const key = scenarioKey(f);
  const [list, setList] = useState<SavedScenario[]>([]);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    let live = true;
    setList([]);
    setNote(null);
    intelScenarios(client, { query: { facility: key }, signal: ctrl.signal })
      .then((r) => {
        if (live) setList(r.scenarios.slice(-6));
      })
      .catch(() => {
        if (live && !ctrl.signal.aborted) setNote('Saved scenarios could not be loaded.');
      });
    return () => {
      live = false;
      ctrl.abort();
    };
  }, [client, key]);
  const save = useCallback(
    (d: ScenarioDraft) => {
      if (readOnly) return;
      intelSaveScenario(client, { body: { facilityKey: key, ...d } })
        .then((r) => {
          setNote(null);
          setList((l) => [...l, r.scenario].slice(-6));
        })
        .catch(() => setNote('Not saved: the server refused the scenario.'));
    },
    [client, key, readOnly],
  );
  return { list, note, save };
}
